import { useState, useEffect, useContext } from 'react';
import { X } from 'lucide-react';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import { ChainlitContext, useChatMessages, threadOperationsEvents, IOperation } from '@chainlit/react-client';

import { operationsService } from '@/services/operationsService';
import { operationsDialogViewState, operationsDialogOpenState } from '@chainlit/react-client';

/**
 * Displays operation badges in the header for the current thread.
 * Shows color-coded badges with quick actions to view or remove operations.
 * Updates in real-time when operations are added, removed, or modified.
 */
export default function ThreadOperationsIndicator() {
  const apiClient = useContext(ChainlitContext);
  const { threadId } = useChatMessages();
  const setDialogView = useSetRecoilState(operationsDialogViewState);
  const setDialogOpen = useSetRecoilState(operationsDialogOpenState);
  
  const [operations, setOperations] = useState<IOperation[]>([]);

  useEffect(() => {
    if (threadId) {
      loadThreadOperations();
    } else {
      setOperations([]);
    }
  }, [threadId]);

  useEffect(() => {
    if (!threadId) return;

    const unsubscribeThreadChanges = threadOperationsEvents.subscribe('thread_operations_changed', (event: any) => {
      if (event.threadId === threadId) {
        loadThreadOperations();
      }
    });

    const unsubscribeOperationsUpdated = threadOperationsEvents.subscribe('operations_updated', () => {
      loadThreadOperations();
    });

    return () => {
      unsubscribeThreadChanges();
      unsubscribeOperationsUpdated();
    };
  }, [threadId]);

  /**
   * Loads all operations that the current thread belongs to.
   */
  const loadThreadOperations = async () => {
    if (!threadId) return;
    
    try {
      const ops = await operationsService.getOperationsForThread(apiClient, threadId);
      setOperations(ops);
    } catch (error) {
      console.error('Failed to load thread operations:', error);
      setOperations([]);
    }
  };

  /**
   * Opens the operations dialog showing the detail view for the selected operation.
   */
  const handleOperationClick = (operationId: string) => {
    setDialogView({ view: 'detail', operationId });
    setDialogOpen(true);
  };

  /**
   * Removes the current thread from the specified operation.
   * Updates UI optimistically and notifies other components via events.
   */
  const handleRemoveFromOperation = async (e: React.MouseEvent, operationId: string, operationName: string) => {
    e.stopPropagation();
    
    if (!threadId) return;
    
    try {
      await operationsService.removeThreadFromOperation(apiClient, operationId, threadId);
      setOperations(prev => prev.filter(op => op.id !== operationId));
      threadOperationsEvents.notifyThreadRemovedFromOperation(threadId, operationId);
      
      toast.success('Removed from operation', {
        description: `Thread removed from "${operationName}"`
      });
    } catch (error) {
      console.error('Failed to remove thread from operation:', error);
      toast.error('Failed to remove from operation', {
        description: 'An error occurred. Please try again.'
      });
    }
  };

  if (!threadId || operations.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {operations.map((operation) => (
        <div
          key={operation.id}
          className="group flex items-center gap-1 px-2 py-1 rounded-md bg-muted/40 hover:bg-primary/10 transition-colors"
        >
          <button
            onClick={() => handleOperationClick(operation.id)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div
              className="w-3 h-3 rounded flex-shrink-0"
              style={{ backgroundColor: operation.color }}
            />
            <span className="text-sm font-medium line-clamp-1">
              {operation.name}
            </span>
          </button>
          <button
            onClick={(e) => handleRemoveFromOperation(e, operation.id, operation.name)}
            className="ml-1 p-0.5 rounded hover:bg-destructive/20 transition-colors opacity-0 group-hover:opacity-100"
            aria-label={`Remove from ${operation.name}`}
            title={`Remove from ${operation.name}`}
          >
            <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
          </button>
        </div>
      ))}
    </div>
  );
}
